/**
 * reserve-modal.js — Modale de réservation Mint Bikes
 *
 * Chargé en defer depuis snippets/reserve-modal.liquid. L'interception se fait
 * en phase CAPTURE sur document : elle l'emporte sur les handlers bubble/directs
 * (product-selling-plan-form, product-form-fmd) quel que soit l'ordre d'attachement.
 *
 * Paiement : Mollie Components (js.mollie.com/v1/mollie.js). Les champs carte sont
 * des iframes Mollie — les données carte ne transitent jamais par ce script (PCI SAQ-A).
 * createToken() renvoie un cardToken opaque, valable 1 h, à envoyer au backend qui
 * appellera POST /v2/payments. Ce backend n'est pas encore branché (cf. TODO submit).
 */

(function () {
  /**
   * Les events de ce fichier partaient en `dataLayer.push` direct, donc SANS passer par
   * `trackEvent` — ils échappaient au miroir `click_cta` de `tracking-core.liquid`, seul
   * moyen aujourd'hui de les faire arriver dans GA4 (aucun des six n'a de déclencheur
   * GTM, vérifié dans les conteneurs le 2026-09-22). Ce relais les y ramène ; le repli
   * `dataLayer.push` conserve le comportement d'origine si `trackEvent` manque.
   */
  function pushEvent(name, data) {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.trackEvent === 'function') {
      window.trackEvent(name, data || {});
      return;
    }
    window.dataLayer.push(Object.assign({ event: name }, data || {}));
  }

  // Idempotence : le listener ne s'attache qu'une seule fois
  if (window.__reserveModalAttached) return;
  window.__reserveModalAttached = true;

  var MOLLIE_JS = 'https://js.mollie.com/v1/mollie.js';

  // Composants Mollie : nom d'API → id du conteneur dans le Liquid.
  // Les 4 sont OBLIGATOIRES : en retirer un fait échouer createToken() avec
  // « Not all required components are mounted yet ». Vérifié en réel.
  var MOLLIE_FIELDS = {
    cardNumber: 'ReserveMollieCardNumber',
    cardHolder: 'ReserveMollieCardHolder',
    expiryDate: 'ReserveMollieExpiry',
    verificationCode: 'ReserveMollieCvc',
  };

  var mollieInstance = null;
  var mollieComponents = {};
  var mollieLoading = null;

  // ---------------------------------------------------------------------------
  // resolveVariantId(btn)
  // Cascade : input[name="id"] du form parent → ?variant= URL → data-variant-id
  // ---------------------------------------------------------------------------
  function resolveVariantId(btn) {
    var formEl = btn.closest('form');
    if (formEl) {
      var variantInput = formEl.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        return String(variantInput.value);
      }
    }
    var urlParams = new URLSearchParams(window.location.search);
    var urlVariant = urlParams.get('variant');
    if (urlVariant) return String(urlVariant);
    return btn.dataset.variantId || null;
  }

  // ---------------------------------------------------------------------------
  // getVariantPriceCents(variantId)
  // Lit [data-variant-json] (textarea) pour retrouver le prix (centimes) du
  // variant résolu. Retourne null si introuvable.
  // ---------------------------------------------------------------------------
  function getVariantPriceCents(variantId) {
    var textareas = document.querySelectorAll('[data-variant-json]');
    for (var i = 0; i < textareas.length; i++) {
      try {
        var variants = JSON.parse(textareas[i].textContent);
        for (var j = 0; j < variants.length; j++) {
          if (String(variants[j].id) === String(variantId)) {
            return variants[j].price;
          }
        }
      } catch (e) {
        // JSON malformé — continuer
      }
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // formatMoney(cents)
  // Utilise theme.Currency.formatMoney si disponible, sinon fallback manuel.
  // ---------------------------------------------------------------------------
  function formatMoney(cents) {
    var text;
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      var moneyFormat = (window.theme && theme.settings && theme.settings.moneyFormat) || '{{amount}} €';
      text = theme.Currency.formatMoney(cents, moneyFormat);
    } else {
      text = (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' €';
    }
    // Espaces insécables : « 3 023,10 € » ne doit se couper ni entre les milliers
    // ni avant le symbole. Pendant du replace fait en Liquid (reserve-modal.liquid).
    // Le caractère U+00A0 en clair est obligatoire — setText écrit en textContent,
    // où une entité &nbsp; s'afficherait telle quelle.
    return text.replace(/ /g, '\u00a0');
  }

  // ---------------------------------------------------------------------------
  // updateAmounts(modal, priceCents) — acompte = min(100 €, 10 % du vélo seul)
  //
  // Le marquage est obligatoire et ajouté au panier, mais il n'est plus encaissé
  // à la réservation : il tombe dans le solde. Son prix vient du Liquid
  // (data-reserve-marquage-cents) et sert encore au total et au solde affichés —
  // ne jamais le coder en dur ici, il changerait sans que le JS le sache.
  // ---------------------------------------------------------------------------
  function updateAmounts(modal, priceCents) {
    if (!priceCents) return;
    var marquageCents = parseInt(modal.dataset.reserveMarquageCents, 10) || 0;
    var orderTotal = priceCents + marquageCents;
    // Formule arrêtée le 2026-07-24 : 100 € fixes, plafonnés à 10 % du VÉLO SEUL
    // (vélo < 1 000 € → les 10 % reprennent la main ; le marquage reste au solde).
    // Ce montant est EXACT partout (remise de LIGNE, plus de DepositInput). Le
    // même chiffre sert ici, chez Mollie et dans la remise — cf. Compute Deposit
    // (n8n) et reserve-modal.liquid : les quatre changent ENSEMBLE.
    var deposit = Math.min(10000, Math.round(priceCents * 0.1));
    var balance = orderTotal - deposit;
    var depositText = formatMoney(deposit);

    setAllText(modal, '[data-reserve-modal-amount]', depositText);
    setAllText(modal, '[data-reserve-modal-submit-amount]', depositText);
    setAllText(modal, '[data-reserve-modal-balance]', formatMoney(balance));
  }

  // textContent (jamais innerHTML) : aucune donnée utilisateur injectée
  function setText(el, value) {
    if (el) el.textContent = value;
  }

  // TOUS les porteurs du marqueur, pas le premier : la ligne du solde est éditable
  // (réglage reserve_modal_price_balance) et le marchand peut y écrire {acompte},
  // qui rend un second [data-reserve-modal-amount] à côté du gros montant. Avec
  // querySelector, ce second-là garderait le montant du variant chargé avec la
  // page — un écran de paiement ne doit pas afficher deux sommes différentes.
  // Aucun nœud trouvé (token absent du texte) : la boucle ne tourne pas.
  function setAllText(root, selector, value) {
    var nodes = root.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = value;
    }
  }

  // Le mot « jour(s) » suit le délai dans le texte du marchand : on accorde le nœud
  // texte voisin, sans toucher au reste de la phrase.
  function syncDayUnit(el, days) {
    var next = el && el.nextSibling;
    if (!next || next.nodeType !== 3) return;
    var one = parseInt(days, 10) === 1;
    next.nodeValue = next.nodeValue.replace(
      /^(\s+)jours?(?![a-zà-ÿ])(?:(\s+)ouvr(?:(ables?)|és?))?/,
      function (m, sp, gap, able) {
        var suffix = '';
        if (gap) suffix = gap + (able ? 'ouvrable' : 'ouvré') + (one ? '' : 's');
        return sp + (one ? 'jour' : 'jours') + suffix;
      },
    );
  }

  // ---------------------------------------------------------------------------
  // updateDeliveryDelay(modal, variantId)
  //
  // Le délai vient du metafield de variante shipping.shortest_delay et VARIE
  // d'une taille à l'autre : sur un même vélo, XS et XL peuvent être à 10 jours
  // quand S/M/L sont à 3. Le Liquid rend le délai du variant chargé avec la page ;
  // si le client change de taille sans rechargement, seul ce JS peut rétablir la
  // vérité — même raison d'être que updateAmounts() pour le montant. Promettre
  // 3 jours dans l'écran où l'on encaisse un acompte, c'est un litige.
  //
  // Source : data-reserve-delays, table « id:jours,id:jours » posée par le Liquid.
  // Aucune table ou aucun variant inconnu → on ne touche à rien, le Liquid fait foi.
  // ---------------------------------------------------------------------------
  function updateDeliveryDelay(modal, variantId) {
    var raw = modal.dataset.reserveDelays;
    if (!raw || !variantId) return;
    var pairs = raw.split(',');
    for (var i = 0; i < pairs.length; i++) {
      var parts = pairs[i].split(':');
      if (parts[0] === String(variantId) && parts[1]) {
        var delayEl = modal.querySelector('[data-reserve-modal-delay]');
        setText(delayEl, parts[1]);
        syncDayUnit(delayEl, parts[1]);
        return;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // readBackendJson(res)
  //
  // n8n renvoie un corps VIDE quand le workflow échoue avant d'atteindre son
  // nœud de réponse — panne Mollie, brouillon refusé, timeout. Un res.json()
  // direct lève alors « Unexpected end of JSON input », qui pointe la ligne du
  // parseur et masque la vraie cause : on cherche le bug chez nous alors que
  // l'incident est distant. Vécu sur un 503 de Mollie le 2026-07-21.
  // On lit donc le texte d'abord, et on n'échoue que sur ce qu'on sait nommer.
  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  // adoptDraft(modal, variantId, data)
  //
  // Le backend renvoie le brouillon MÊME quand Mollie refuse le paiement. On le
  // mémorise alors, sinon le réessai repart à froid et le backend en crée un
  // second : trois tentatives = trois brouillons orphelins (vécu le 2026-07-21
  // sur les 503 Google Pay). Sur un paiement qui aboutit, c'est clearStoredDraft
  // qui reprend la main plus loin — le brouillon devient une commande.
  // ---------------------------------------------------------------------------
  function adoptDraft(modal, variantId, data) {
    if (!data || !data.draftOrderId) return;
    modal.dataset.reserveDraftId = data.draftOrderId;
    storeDraft(variantId, data.draftOrderId);
  }

  function readBackendJson(res) {
    return res.text().then(function (raw) {
      if (!raw) {
        throw new Error('Backend sans réponse (HTTP ' + res.status + ') — le workflow a échoué avant de répondre');
      }
      var data;
      try {
        data = JSON.parse(raw);
      } catch (e) {
        throw new Error('Backend illisible (HTTP ' + res.status + ') : ' + raw.slice(0, 200));
      }
      if (!res.ok) {
        throw new Error('Backend en erreur (HTTP ' + res.status + ') : ' + (data.message || raw.slice(0, 200)));
      }
      return data;
    });
  }

  function setStatus(modal, message, isError) {
    var el = modal.querySelector('[data-reserve-modal-status]');
    if (!el) return;
    if (!message) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.textContent = message;
    el.classList.toggle('reserve-modal__status--error', isError === true);
    el.hidden = false;
  }

  // ---------------------------------------------------------------------------
  // Réservation déjà en cours : une seule résa express active par e-mail.
  // {heures} se calcule sur l'échéance annoncée au client (paiement + 72 h) ;
  // au-delà, le texte « arrive à expiration » prend le relais ; échéance inconnue
  // ou réglage inutilisable : texte générique.
  // ---------------------------------------------------------------------------
  function isDuplicateReservation(data) {
    return Boolean(data && data.error === 'reservation_en_cours');
  }

  // null = échéance illisible, '' = dépassée, sinon le libellé des heures restantes.
  function hoursLeft(deadlineIso) {
    var deadline = Date.parse(deadlineIso || '');
    if (isNaN(deadline)) return null;
    var left = deadline - Date.now();
    if (!(left > 0)) return '';
    var hours = Math.floor(left / 3600000);
    return hours < 1 ? 'moins d’1' : String(hours);
  }

  function duplicateText(modal, hours, phone) {
    var text = '';
    if (hours) text = modal.dataset.reserveDuplicateText || '';
    else if (hours === '') text = modal.dataset.reserveDuplicateExpiringText || '';
    text = text.replace(/\{heures\}/g, hours || '');
    // Échéance inconnue, réglage vidé ou numéro absent : texte générique, jamais « au  » orphelin.
    if (!text.trim() || (!phone && text.indexOf('{telephone}') !== -1)) {
      text =
        'Vous avez déjà une réservation en cours avec cet e-mail.' +
        (phone ? ' Appelez un conseiller au {telephone}.' : '');
    }
    return text;
  }

  // Réponse du backend de paiement, commune aux chemins carte et wallet.
  function handlePaymentResponse(modal, variantId, data, method, reenable) {
    adoptDraft(modal, variantId, data);
    if (isDuplicateReservation(data)) {
      showDuplicateReservation(modal, data, method);
      reenable();
      return;
    }
    if (!data || !data.checkoutUrl) {
      throw new Error(data && data.error ? 'Backend : ' + data.error : 'Réponse backend invalide');
    }
    goToCheckout(modal, variantId, data, reenable);
  }

  function appendWithPhone(el, text, phone) {
    text.split('{telephone}').forEach(function (part, i) {
      if (i > 0 && phone) {
        var link = document.createElement('a');
        link.href = 'tel:' + phone.replace(/[\s.]/g, '');
        link.textContent = phone;
        link.setAttribute('data-cta-name', 'reserve_duplicate_call');
        el.appendChild(link);
      }
      el.appendChild(document.createTextNode(part));
    });
  }

  function showDuplicateReservation(modal, data, method) {
    var el = modal.querySelector('[data-reserve-modal-status]');
    if (!el) return;
    var phone = modal.dataset.reserveDuplicatePhone || '';
    el.textContent = '';
    appendWithPhone(el, duplicateText(modal, hoursLeft(data.echeanceClient), phone), phone);
    el.classList.add('reserve-modal__status--error');
    el.hidden = false;
    el.focus();
    pushModalCta(modal, 'reserve_blocked_duplicate', null, method || 'card');
  }

  // ---------------------------------------------------------------------------
  // loadMollieScript() — injecte js.mollie.com une seule fois, renvoie une Promise
  // ---------------------------------------------------------------------------
  function loadMollieScript() {
    if (window.Mollie) return Promise.resolve();
    if (mollieLoading) return mollieLoading;

    mollieLoading = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = MOLLIE_JS;
      script.async = true;
      script.onload = resolve;
      script.onerror = function () {
        reject(new Error('Mollie script failed to load'));
      };
      document.head.appendChild(script);
    });
    return mollieLoading;
  }

  // ---------------------------------------------------------------------------
  // mountMollie(modal) — monte les 4 composants carte (idempotent)
  // Les styles sont passés en JS : les iframes Mollie ne héritent pas du CSS
  // de la page, il faut donc redéclarer la typo des champs ici.
  // ---------------------------------------------------------------------------
  function mountMollie(modal) {
    var profileId = modal.dataset.mollieProfileId;
    if (!profileId) {
      console.warn('[reserve-modal] settings.mollie_profile_id vide — champs carte désactivés.');
      enableHostedFallback(modal);
      return Promise.resolve(false);
    }
    // Les composants sont démontés à la fermeture mais l'instance survit :
    // si elle existe déjà, il ne reste qu'à recréer les champs.
    if (mollieInstance && Object.keys(mollieComponents).length) {
      return Promise.resolve(true);
    }

    return loadMollieScript()
      .then(function () {
        // `new Mollie(...)` UNE SEULE FOIS par page (cf. unmountMollie).
        if (!mollieInstance) {
          mollieInstance = window.Mollie(profileId, {
            locale: 'fr_FR',
            testmode: modal.dataset.mollieTestmode === 'true',
          });
        }

        var options = {
          styles: {
            base: {
              color: '#1a1a1a',
              fontSize: '14px',
              fontFamily: 'inherit',
              '::placeholder': { color: '#757575' },
            },
            valid: { color: '#1a1a1a' },
            invalid: { color: '#d0342c' },
          },
        };

        Object.keys(MOLLIE_FIELDS).forEach(function (name) {
          var container = document.getElementById(MOLLIE_FIELDS[name]);
          if (!container) return;
          var component = mollieInstance.createComponent(name, options);
          component.mount('#' + MOLLIE_FIELDS[name]);
          mollieComponents[name] = component;
          bindFieldState(modal, component, container, name);
        });

        return true;
      })
      .catch(function (err) {
        console.error('[reserve-modal] Mollie init failed:', err);
        enableHostedFallback(modal);
        return false;
      });
  }

  // ---------------------------------------------------------------------------
  // enableHostedFallback — plan B quand le widget carte ne peut pas s'afficher
  //
  // js.mollie.com bloqué (adblock, webview d'appli, réseau d'entreprise) ne doit
  // pas tuer la vente : le backend accepte un paiement carte SANS cardToken, et
  // Mollie fait alors saisir la carte sur SA page. On masque les champs morts et
  // le clic sur « Payer » part en checkout hébergé (cf. handleSubmit).
  // ---------------------------------------------------------------------------
  function enableHostedFallback(modal) {
    if (modal.classList.contains('reserve-modal--hosted')) return;
    modal.classList.add('reserve-modal--hosted');
    setStatus(modal, 'La saisie de votre carte s’ouvrira sur la page de paiement sécurisée Mollie.');
    window.dataLayer = window.dataLayer || [];
    pushEvent('reserve_hosted_fallback', {
      reserve_modal: true,
      variant_id: modal.dataset.reserveVariantId || '',
    });
  }

  // Erreur + état focus/invalide portés sur le conteneur (l'iframe ne peut pas être stylée)
  function bindFieldState(modal, component, container, name) {
    var errorEl = modal.querySelector('[data-mollie-error="' + name + '"]');
    component.addEventListener('change', function (event) {
      var showError = Boolean(event.error) && event.touched === true;
      if (errorEl) errorEl.textContent = showError ? event.error : '';
      container.classList.toggle('is-invalid', showError);
    });
    component.addEventListener('focus', function () {
      container.classList.add('is-focused');
    });
    component.addEventListener('blur', function () {
      container.classList.remove('is-focused');
    });
  }

  // On démonte les COMPOSANTS (les iframes des champs carte) mais on conserve
  // l'instance Mollie : `new Mollie(...)` ne doit être appelé qu'une seule fois
  // par page, sinon Mollie avertit « initialized multiple times » et le
  // comportement devient imprévisible. Ici la modale peut être rouverte
  // plusieurs fois — seuls les composants sont recréés.
  // Réf. https://docs.mollie.com/components/overview#initialize-the-mollie-object
  function unmountMollie() {
    Object.keys(mollieComponents).forEach(function (name) {
      try {
        mollieComponents[name].unmount();
      } catch (e) {
        // composant déjà démonté — ignorer
      }
    });
    mollieComponents = {};
  }

  // ---------------------------------------------------------------------------
  // fireReserveTracking(variantId)
  // Notre stopImmediatePropagation en phase capture NEUTRALISE le tracker GTM
  // délégué (bubble) de tracking-ecommerce.liquid : il ne s'exécute jamais.
  // Ce push est donc le SEUL signal reserve_in_store.
  // ---------------------------------------------------------------------------
  function fireReserveTracking(variantId) {
    if (!window.dataLayer) return;
    var payload = { event: 'reserve_in_store', reserve_modal: true };
    if (window.productData) {
      payload.item_id = window.productData.id;
      payload.item_brand = window.productData.brand;
      payload.option_fmd = window.productData.fmd;
      payload.promotion = window.productData.promo;
    }
    if (variantId) payload.variant_id = String(variantId);
    window.dataLayer.push(payload);
  }

  // E-mail complet saisi = signal de panier abandonné (cf. discovery §8)
  function fireEmailCaptured(modal, email) {
    if (!window.dataLayer || !email) return;
    if (modal.dataset.reserveEmailFired === email) return;
    modal.dataset.reserveEmailFired = email;
    pushEvent('reserve_email_captured', {
      reserve_modal: true,
      variant_id: modal.dataset.reserveVariantId || '',
    });
  }

  // ---------------------------------------------------------------------------
  // Focus trap Tab/Shift+Tab dans la modale
  // ---------------------------------------------------------------------------
  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var modal = document.getElementById('ReserveModalOverlay');
    if (!modal) return;
    var all = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    // Exclure les éléments désactivés ou masqués (offsetParent null = display:none)
    var focusable = Array.prototype.filter.call(all, function (el) {
      return !el.disabled && el.offsetParent !== null;
    });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function handleKeydown(e) {
    if (e.key === 'Escape') {
      closeModal();
    } else {
      trapFocus(e);
    }
  }

  // ---------------------------------------------------------------------------
  // isModalExperienceEnabled() — kill switch éditeur
  //
  // Lu à CHAQUE clic, jamais mis en cache : bascule le réglage dans l'éditeur,
  // et le comportement change au clic suivant sans recharger la page.
  // La modale absente du DOM vaut « désactivée » — repli sur le drawer.
  // ---------------------------------------------------------------------------
  function isModalExperienceEnabled() {
    var modal = document.getElementById('ReserveModalOverlay');
    if (!modal) return false;
    if (modal.dataset.reserveExperience === 'drawer') return false;

    // Restriction par appareil : hors de la cible, on rend la main au drawer
    // plutôt que d'ouvrir une modale que le CSS masque (cf. reserve_modal_devices).
    var devices = modal.dataset.reserveDevices || 'all';
    if (devices === 'all') return true;
    var isMobile = window.matchMedia('(max-width: 767px)').matches;
    return devices === 'mobile' ? isMobile : !isMobile;
  }

  // Callbacks à jouer quand le client quitte la modale sans payer (panier abandonné).
  var modalExitHandlers = [];

  function isModalOpen(modal) {
    return Boolean(modal) && modal.hidden === false;
  }

  function closeModal() {
    var modal = document.getElementById('ReserveModalOverlay');
    if (!modal) return;
    // AVANT de masquer : les handlers lisent l'état du formulaire.
    modalExitHandlers.forEach(function (fn) {
      try {
        fn();
      } catch (e) {
        /* le panier abandonné ne doit jamais empêcher la fermeture */
      }
    });
    modal.hidden = true;
    document.body.style.overflow = '';
    document.documentElement.classList.remove('reserve-modal-is-open');
    document.removeEventListener('keydown', handleKeydown);
    unmountMollie();
    setStatus(modal, '');
    // Rendre le focus au bouton déclencheur
    if (window.__reserveModalTriggerBtn) {
      window.__reserveModalTriggerBtn.focus();
      window.__reserveModalTriggerBtn = null;
    }
  }

  function openModal(btn, variantId) {
    var modal = document.getElementById('ReserveModalOverlay');
    if (!modal) return;
    if (!modal.hidden) return; // déjà ouverte — éviter un double-déclenchement

    window.__reserveModalTriggerBtn = btn;
    if (variantId) modal.dataset.reserveVariantId = variantId;

    // Prix du variant (variant-json → fallback data-attribute)
    var priceCents = null;
    if (variantId) priceCents = getVariantPriceCents(variantId);
    if (!priceCents) priceCents = parseInt(modal.dataset.reservePriceCents, 10) || null;
    if (priceCents) {
      modal.dataset.reservePriceCents = priceCents;
      updateAmounts(modal, priceCents);
    }
    updateDeliveryDelay(modal, variantId);

    var form = modal.querySelector('[data-reserve-modal-form]');
    if (form) {
      // reset() vide aussi les champs Mollie ; on restaure ensuite ce que le
      // client avait déjà saisi pour qu'il n'ait pas à le retaper.
      form.reset();
      restoreContact(form);
    }
    // La case « visite en boutique » repart décochée à chaque ouverture : elle
    // change tout l'écran (plus de Mollie, plus d'acompte, autre montant affiché),
    // un état hérité en silence d'une ouverture précédente serait un piège.
    // form.reset() décoche la case ; cette ligne remet la classe en accord.
    syncPickupMode(modal);
    delete modal.dataset.reserveEmailFired;
    // Un panier abandonné ne se justifie que si le client a saisi quelque chose
    // PENDANT cette ouverture : sans ça, des coordonnées restaurées suffiraient à
    // en créer un à chaque ouverture/fermeture, y compris après une commande.
    delete modal.dataset.reserveTouched;
    // Retour en arrière depuis Mollie : la modale redevient synchronisable.
    delete modal.dataset.reserveLeaving;
    setStatus(modal, '');

    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    // Masque le chat HubSpot, qui se pose au z-index maximal et passerait devant
    // la modale (cf. reserve-modal.liquid pour le pourquoi du détour par une classe).
    document.documentElement.classList.add('reserve-modal-is-open');
    document.addEventListener('keydown', handleKeydown);

    // Monter Mollie une fois la modale visible (les iframes ont besoin de dimensions)
    mountMollie(modal);

    // Pose la ligne miroir au panier : c'est ce qui rend la reservation express
    // visible de l'instrumentation Shopify et de GA4 (cf. bloc LIGNE MIROIR).
    // Volontairement non bloquant — l'ouverture de la modale ne depend pas du reseau.
    addMirrorLine(modal.dataset.reserveVariantId);

    // En mobile, focaliser le premier champ ouvre le clavier virtuel, qui recouvre
    // aussitôt la moitié de la modale : le client arrive sur un écran déjà mangé.
    // On focalise alors le dialog lui-même (tabindex="-1") — le piège à focus et
    // la touche Échap continuent de fonctionner, sans clavier.
    var target = modal;
    if (!window.matchMedia('(max-width: 767px)').matches) {
      target = modal.querySelector('.reserve-modal__input') || modal;
    }
    // rAF : focus une fois l'affichage appliqué par le navigateur
    window.requestAnimationFrame(function () {
      target.focus();
    });
  }

  // ---------------------------------------------------------------------------
  // Visite en boutique — « je viens voir le vélo sur place chez MINT »
  //
  // Le client vient voir le vélo avant d'acheter : on rejoint le parcours « Acheter » (article
  // NU, sans selling plan), puis on pose `acompte_souhaite` et la préférence Retrait que lit
  // l'extension `acompte_checkout`. La destination suit le réglage « Rediriger vers le
  // paiement » (theme.routes.cartRedirect) ; la modale affiche le TOTAL, que le checkout prélève.
  // Le retrait « chez MINT Bikes » y est proposé comme mode de livraison : c'est là
  // que le choix se matérialise, la case ne fait que l'aiguillage.
  //
  // On redirige TOUJOURS, même en cart_type « drawer » : ouvrir un tiroir de
  // panier derrière une modale de paiement laisserait le client devant deux
  // surfaces empilées sans savoir laquelle est active.
  //
  // Ni brouillon, ni remise ACOMPTE, ni paiement Mollie : cette commande est
  // ordinaire. C'est beginCheckout() qui l'assure — il coupe les synchros de lead
  // en vol, sans quoi un brouillon « ACOMPTE EXPRESS » à remise resterait derrière
  // une commande au prix plein, et un vendeur pourrait le compléter (anti-pattern
  // n°11 : le client paierait alors son acompte une seconde fois).
  // Un brouillon déjà créé à la saisie de l'e-mail, lui, reste tagué « PANIER
  // ABANDONNE » et suit le sort de n'importe quel abandon — ce qu'il est.
  // ---------------------------------------------------------------------------
  var PICKUP_CLASS = 'reserve-modal--pickup';

  function pickupInput(modal) {
    return modal.querySelector('[data-reserve-modal-pickup]');
  }

  function isPickupChecked(modal) {
    var input = pickupInput(modal);
    return Boolean(input && input.checked);
  }

  // La bascule est une CLASSE et non un `hidden` par bloc : les zones à masquer
  // déclarent toutes un `display`, contre lequel le `[hidden]` du navigateur perd
  // (cf. anti-pattern n°8). Le CSS vit dans reserve-modal.liquid.
  function syncPickupMode(modal) {
    modal.classList.toggle(PICKUP_CLASS, isPickupChecked(modal));
  }

  // Quantité du formulaire produit qui a ouvert la modale — même lecture que le
  // handler « Réserver » historique (product-selling-plan-form.liquid).
  function readQuantity() {
    var trigger = window.__reserveModalTriggerBtn;
    var formEl = trigger ? trigger.closest('form') : null;
    var input = formEl ? formEl.querySelector('input[name="quantity"]') : null;
    var qty = input ? parseInt(input.value, 10) : 1;
    return !qty || qty < 1 ? 1 : qty;
  }

  // ---------------------------------------------------------------------------
  // LIGNE MIROIR DE LA RÉSERVATION EXPRESS
  //
  // Le flux nominal de cette modale ne touche JAMAIS au panier Shopify : il encaisse
  // un acompte chez Mollie et crée un draft order. La réservation express était donc
  // structurellement invisible pour l'instrumentation Shopify, qui n'observe que de
  // vrais /cart/add. On pose donc une ligne à l'ouverture, on la retire à la
  // fermeture — et l'add_to_cart part tout seul, via l'intercepteur de
  // tracking-ecommerce.liquid, avec atc_type = 'resa_express'.
  //
  // Invariants, tous tirés de pièges déjà rencontrés dans ce fichier :
  //  - Vélo DÉJÀ au panier (« Acheter » puis « Réserver » depuis la modale ATC) : on
  //    ne pose rien. L'add_to_cart a déjà été émis en 'acheter', et
  //    goToShopifyCheckout compte sur cet exemplaire — en ajouter un second
  //    facturerait deux vélos.
  //  - On RESTAURE la quantité précédente au lieu de supprimer : /cart/add.js fusionne
  //    les lignes de même variante, une suppression sèche effacerait des exemplaires
  //    que le client avait mis lui-même.
  //  - Le marquage est une ligne PAYANTE calquée sur le nombre de vélos
  //    (cart-drawer-reserve.liquid) : on resynchronise après l'ajout ET après le
  //    retrait, sinon on facture un marquage orphelin.
  // ---------------------------------------------------------------------------
  var MIRROR_STORAGE_KEY = 'mintReserveMirrorLine';
  var mirrorLine = null; // { key, prevQty, item } | null
  var mirrorPending = null; // promesse de l'ajout encore en vol
  var mirrorDisarmed = false; // le client est parti au checkout : ne plus rien armer

  function cartRoute(name, fallback) {
    return (window.theme && theme.routes && theme.routes[name]) || fallback;
  }

  function postCartJson(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.json();
    });
  }

  function syncMarquageSafe() {
    var sync = window.theme && theme.cart && theme.cart.syncMarquage;
    if (typeof sync !== 'function') return Promise.resolve();
    return sync().catch(function () {});
  }

  // Sous-ensemble suffisant pour que l'émetteur GA4 reconstruise l'item : inutile de
  // stocker l'objet ligne complet (URLs CDN comprises) en sessionStorage.
  function trimMirrorLine(line) {
    return {
      product_id: line.product_id,
      product_title: line.product_title,
      product_type: line.product_type,
      variant_title: line.variant_title,
      vendor: line.vendor,
      price: line.price,
      quantity: 1,
      properties: line.properties || null,
    };
  }

  function rememberMirror(state) {
    try {
      window.sessionStorage.setItem(MIRROR_STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* sessionStorage indisponible — on perd le filet, pas la fonctionnalité */
    }
  }

  function forgetMirror() {
    try {
      window.sessionStorage.removeItem(MIRROR_STORAGE_KEY);
    } catch (e) {
      /* idem */
    }
  }

  function addMirrorLine(variantId) {
    if (!variantId) return;
    if (window.__mintAtcCartKey) return; // vélo déjà au panier, cf. invariant 1
    if (mirrorLine || mirrorPending) return;

    mirrorDisarmed = false;

    // Sentinelle posée SYNCHRONEMENT. goToShopifyCheckout la lit pour savoir qu'un
    // exemplaire est déjà — ou va être — au panier. L'armer seulement au retour du
    // fetch laisserait une fenêtre pendant laquelle « visite en boutique » ajouterait
    // un SECOND vélo. Elle est retirée si l'ajout échoue.
    window.__mintAtcCartKey = true;

    mirrorPending = fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var existing = (cart.items || []).filter(function (it) {
          return String(it.variant_id) === String(variantId);
        })[0];
        var prevQty = existing ? existing.quantity : 0;

        window.__mintAtcType = 'resa_express';
        return postCartJson(cartRoute('cartAdd', '/cart/add.js'), { id: variantId, quantity: 1 }).then(function (line) {
          if (!line || line.status === 422 || !line.key) {
            delete window.__mintAtcType;
            delete window.__mintAtcCartKey;
            return null;
          }
          window.__mintAtcCartKey = line.key;
          // Parti au checkout pendant que l'ajout était en vol : la ligne doit
          // rester, on n'arme ni le retrait ni le filet de session.
          if (mirrorDisarmed) return syncMarquageSafe();

          mirrorLine = { key: line.key, prevQty: prevQty, item: trimMirrorLine(line) };
          rememberMirror(mirrorLine);
          return syncMarquageSafe();
        });
      })
      .catch(function (e) {
        console.error('[reserve-modal] ligne miroir non posée:', e);
        delete window.__mintAtcType;
        delete window.__mintAtcCartKey;
      })
      .then(function () {
        mirrorPending = null;
      });
  }

  function removeMirrorLine() {
    // L'ajout peut être encore en vol si le client referme aussitôt : on chaîne
    // dessus, sinon le retrait partirait avant que la ligne n'existe.
    return (mirrorPending || Promise.resolve())
      .then(function () {
        if (!mirrorLine) return;
        var target = mirrorLine;
        mirrorLine = null;
        forgetMirror();
        if (window.__mintAtcCartKey === target.key) delete window.__mintAtcCartKey;

        return postCartJson(cartRoute('cartChange', '/cart/change.js'), {
          id: target.key,
          quantity: target.prevQty,
        }).then(function () {
          if (typeof window.mintTrackRemoveFromCart === 'function') {
            window.mintTrackRemoveFromCart([target.item], 'resa_express');
          }
          return syncMarquageSafe();
        });
      })
      .catch(function (e) {
        console.error('[reserve-modal] ligne miroir non retirée:', e);
      });
  }

  // La ligne part au checkout Shopify avec le client (« visite en boutique ») : on
  // l'oublie SANS la retirer, et on efface le marqueur de session — sinon le filet
  // ci-dessous la supprimerait au retour du client sur une fiche produit.
  function keepMirrorLine() {
    mirrorDisarmed = true;
    mirrorLine = null;
    forgetMirror();
  }

  function onModalExitMirror() {
    // Départ vers un checkout Shopify : la ligne doit rester. Toutes les autres
    // sorties (croix, Échap, backdrop) sont des abandons — on rend alors le panier à
    // l'état exact qu'il avait avant l'ouverture.
    var modal = document.getElementById('ReserveModalOverlay');
    if (modal && isLeavingForCheckout(modal)) return;
    removeMirrorLine();
  }

  // Filet : la modale peut disparaître sans passer par closeModal — onglet fermé,
  // navigation arrière, redirection vers Mollie. `beforeunload` ne sauverait rien (un
  // fetch sans keepalive est avorté au unload), on nettoie donc au chargement suivant.
  // C'est aussi ce qui retire le vélo après un acompte RÉELLEMENT payé : la
  // réservation vit comme draft order, laisser le vélo au panier exposerait le client
  // à le repayer au prix fort au checkout suivant.
  function cleanupOrphanMirror() {
    var raw = null;
    try {
      raw = window.sessionStorage.getItem(MIRROR_STORAGE_KEY);
    } catch (e) {
      return;
    }
    if (!raw) return;
    forgetMirror();

    var state = null;
    try {
      state = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!state || !state.key) return;

    postCartJson(cartRoute('cartChange', '/cart/change.js'), { id: state.key, quantity: state.prevQty || 0 })
      .then(function (cart) {
        // Clé inconnue (panier vidé ou commandé entre-temps) : Shopify renvoie un
        // objet d'erreur. Il n'y a alors rien à signaler — la ligne n'existe plus,
        // le but est atteint.
        if (!cart || cart.status) return;
        if (state.item && typeof window.mintTrackRemoveFromCart === 'function') {
          window.mintTrackRemoveFromCart([state.item], 'resa_express');
        }
        return syncMarquageSafe();
      })
      .catch(function (e) {
        console.error('[reserve-modal] nettoyage ligne miroir échoué:', e);
      });
  }

  modalExitHandlers.push(onModalExitMirror);
  cleanupOrphanMirror();

  // ---------------------------------------------------------------------------
  // PRÉREMPLISSAGE DU CHECKOUT SHOPIFY
  //
  // En mode visite, les coordonnées saisies ici ne servent plus à rien côté MINT
  // (aucun acompte, aucun lead) : on les passe donc au checkout, qui les
  // redemanderait sinon. Elles y sont FACULTATIVES — handleSubmit saute
  // checkValidity dans ce mode —, donc on n'envoie que ce qui est rempli et valide :
  // préremplir une adresse cassée coûte plus cher que ne rien préremplir, le client
  // la validant d'un clic sans la relire.
  //
  // Mesuré le 2026-09-16 sur le checkout de mintbikas.com (checkout récent, URL
  // /checkouts/cn/…), qui accepte toujours ces paramètres malgré ce qu'on lit sur
  // Checkout Extensibility :
  //   checkout[email]                        → e-mail
  //   checkout[shipping_address][first_name] → prénom (contact ET adresse)
  //   checkout[shipping_address][last_name]  → nom
  //   checkout[shipping_address][phone]      → téléphone
  // checkout[delivery_method]=pick_up, lui, est IGNORÉ : le retrait chez MINT reste
  // à choisir à la main dans le checkout.
  // ---------------------------------------------------------------------------

  // Le champ téléphone de la modale n'impose aucun format, mais Shopify déduit
  // l'indicatif du PAYS de l'adresse : sans pays, « 0612345678 » est rendu
  // « +1 0612345678 » (mesuré). On ne transmet donc qu'un numéro qu'on sait mettre
  // en forme internationale — les autres partent à la poubelle plutôt qu'au checkout.
  function phoneForCheckout(raw, countryCode) {
    var v = String(raw || '').replace(/[\s.\-()]/g, '');
    if (v.indexOf('00') === 0) v = '+' + v.slice(2);
    if (v.charAt(0) === '+') {
      return /^\+[0-9]{8,15}$/.test(v) ? v : '';
    }
    // Numéro national : seul le plan français est connu ici.
    if (countryCode !== 'FR') return '';
    return /^0[1-9][0-9]{8}$/.test(v) ? '+33' + v.slice(1) : '';
  }

  function checkoutUrlWithContact(modal) {
    var dest = (window.theme && theme.routes && theme.routes.cartRedirect) || '/checkout';
    // Le réglage « Rediriger vers le paiement » peut renvoyer sur /cart, où ces
    // paramètres ne seraient pas lus : on ne les accroche qu'au vrai checkout.
    if (dest.indexOf('/checkout') === -1) return dest;

    var form = modal.querySelector('[data-reserve-modal-form]');
    if (!form) return dest;

    var params = [];
    function push(key, value) {
      if (!value) return;
      params.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
    }

    // checkValidity() plutôt qu'un simple test de non-vide : le champ porte un
    // `pattern` qui exige un TLD (cf. reserve-modal.liquid), et une adresse
    // injoignable préremplie au checkout ne laisse au client ni confirmation ni
    // lien de suivi.
    var email = form.email && form.email.checkValidity() ? form.email.value.trim() : '';
    push('checkout[email]', email);
    push('checkout[shipping_address][first_name]', form.prenom ? form.prenom.value.trim() : '');
    push('checkout[shipping_address][last_name]', form.nom ? form.nom.value.trim() : '');

    var phone = phoneForCheckout(form.tel ? form.tel.value : '', modal.dataset.reserveCountry || 'FR');
    if (phone) {
      push('checkout[shipping_address][phone]', phone);
      // Le pays n'accompagne QUE le numéro français : c'est lui qui fait rendre
      // « +33 6 12 34 56 78 » au lieu d'un indicatif américain. L'envoyer à côté
      // d'un numéro étranger préremplirait un mauvais pays de livraison.
      if (phone.indexOf('+33') === 0) push('checkout[shipping_address][country]', 'France');
    }

    if (params.length === 0) return dest;
    return dest + (dest.indexOf('?') === -1 ? '?' : '&') + params.join('&');
  }

  // Présélectionne « Retrait » au checkout. Best effort : un échec laisse le client
  // choisir lui-même le mode de livraison.
  function preferPickupAtCheckout(cartToken) {
    var setPreference = window.theme && theme.cart && theme.cart.setDeliveryPreference;
    if (!cartToken || typeof setPreference !== 'function') return Promise.resolve(false);
    return setPreference(cartToken, ['PICK_UP']);
  }

  function goToShopifyCheckout(modal, btn) {
    var variantId = modal.dataset.reserveVariantId;
    if (!variantId) {
      setStatus(modal, 'Une erreur est survenue. Merci de réessayer.', true);
      return;
    }

    if (btn) btn.disabled = true;
    setStatus(modal, 'Ouverture du paiement…');
    beginCheckout(modal);
    // Le velo part au checkout avec le client : la ligne miroir doit rester en place,
    // et le filet de session ne doit pas la supprimer plus tard.
    keepMirrorLine();
    firePickupCheckout(modal, variantId);

    // PASSATION DEPUIS LA MODALE « Ajouté au panier ».
    //
    // Cet ajout est correct quand le client clique « Réserver » depuis la fiche : le
    // vélo n'est alors PAS encore au panier. Mais /cart/add.js est ADDITIF, et la
    // modale ATC amène ici avec la ligne DÉJÀ posée (elle ne la retire pas en passant
    // la main — supprimer une ligne fusionnée effacerait des exemplaires que le client
    // avait mis avant). Sans ce test, le parcours « Acheter → Réserver → je viens voir
    // le vélo sur place » facturait DEUX vélos et deux marquages.
    //
    // Le marqueur est une variable de page : toute navigation le détruit, il ne peut
    // donc pas survivre à un rechargement et fausser un parcours ultérieur. Il est
    // consommé ici même, à la première lecture.
    var atcAlreadyInCart = window.__mintAtcCartKey;
    delete window.__mintAtcCartKey;

    var cartAdd = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';
    var cartRead = (window.theme && theme.routes && theme.routes.cart) || '/cart.js';

    function addToCart() {
      return fetch(cartAdd, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: readQuantity() }] }),
      }).then(function (res) {
        return res.json();
      });
    }

    // Le marqueur ci-dessus ne couvre QUE la passation directe depuis la modale ATC :
    // c'est une variable de page, donc le moindre rechargement la detruit. Tous les
    // autres chemins qui arrivent ici avec le velo DEJA au panier — ajoute plus tot,
    // session precedente, retour arriere, second onglet — passaient a travers et
    // ajoutaient un SECOND exemplaire. Sur un velo en stock 1 (le cas courant ici)
    // Shopify repond 422 et le client voit « Une erreur est survenue » ; en stock 2+
    // il ne voit RIEN et part payer deux velos et deux marquages. Signale le
    // 2026-08-27 depuis un panier deja garni. On lit donc le panier avant d'ajouter,
    // au lieu de se fier a un marqueur qui ne survit pas a une navigation.
    var addStep = atcAlreadyInCart
      ? Promise.resolve(null)
      : fetch(cartRead, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
          .then(function (res) {
            return res.json();
          })
          .then(function (cart) {
            var lignes = (cart && cart.items) || [];
            var deja = lignes.some(function (item) {
              return String(item.id) === String(variantId);
            });
            // Deja au panier : on n'ajoute rien. Le client repart avec la ligne
            // qu'il avait, jamais avec un doublon. On ne retire pas non plus la
            // ligne existante : elle peut porter des exemplaires voulus.
            return deja ? null : addToCart();
          })
          .catch(function () {
            // Panier illisible : ne pas bloquer le parcours pour autant. On
            // retombe sur l'ancien comportement, 422 compris (gere juste apres).
            return addToCart();
          });

    addStep
      .then(function (data) {
        if (data && data.status === 422) {
          throw new Error(data.description || 'Article indisponible');
        }
        // Le marquage est obligatoire, un par vélo, et facturé à 100 % : sans
        // cette synchro la commande partirait sans lui. Même appel que le bouton
        // « Acheter » et que le handler « Réserver » historique.
        if (window.theme && theme.cart && typeof theme.cart.syncMarquage === 'function') {
          return theme.cart.syncMarquage();
        }
      })
      .then(function () {
        // L'extension d'acompte lit cet attribut. Jamais de selling plan depuis le thème
        // (anti-patterns n°53) ; un échec ou un blocage ne doit pas retarder la redirection.
        var update = postCartJson(cartRoute('cartUpdate', '/cart/update.js'), {
          attributes: { acompte_souhaite: '1' },
        }).catch(function () {
          return null;
        });
        return theme.cart.withTimeout(update, 3000, null);
      })
      .then(function (cart) {
        return preferPickupAtCheckout(cart && cart.token);
      })
      .then(function () {
        window.location.assign(checkoutUrlWithContact(modal));
      })
      .catch(function (err) {
        console.error('[reserve-modal] visite en boutique échouée:', err);
        setStatus(modal, 'Une erreur est survenue. Merci de réessayer.', true);
        // On n'est plus en partance : la modale redevient synchronisable, et le
        // client peut décocher pour repartir sur l'acompte.
        delete modal.dataset.reserveLeaving;
        if (btn) btn.disabled = false;
      });
  }

  function firePickupToggle(modal, checked) {
    if (!window.dataLayer) return;
    pushEvent('reserve_pickup_toggle', {
      reserve_modal: true,
      pickup: checked === true,
      variant_id: modal.dataset.reserveVariantId || '',
    });
  }

  // Départ vers le checkout Shopify. L'`add_to_cart` GA4, lui, part tout seul :
  // tracking-ecommerce.liquid intercepte les appels à /cart/add.
  function firePickupCheckout(modal, variantId) {
    if (!window.dataLayer) return;
    pushEvent('reserve_pickup_checkout', {
      reserve_modal: true,
      variant_id: String(variantId || ''),
    });
  }

  // ---------------------------------------------------------------------------
  // handleSubmit — valide les coordonnées puis demande le cardToken à Mollie
  // ---------------------------------------------------------------------------
  function handleSubmit(e) {
    e.preventDefault();
    var form = e.currentTarget;
    var modal = document.getElementById('ReserveModalOverlay');
    if (!modal) return;

    // AVANT checkValidity() : en mode visite, les coordonnées ne sont plus
    // exigées — le checkout Shopify les collecte lui-même, les redemander ici
    // serait une friction pour rien.
    if (isPickupChecked(modal)) {
      goToShopifyCheckout(modal, modal.querySelector('[data-reserve-modal-submit]'));
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    if (!mollieInstance) {
      if (modal.classList.contains('reserve-modal--hosted')) {
        // Widget carte indisponible : même départ que les wallets, sans
        // cardToken — la carte sera saisie sur la page hébergée Mollie.
        var hostedBtn = modal.querySelector('[data-reserve-modal-submit]');
        payWithWallet(modal, form, hostedBtn, 'creditcard');
      } else {
        setStatus(modal, 'Le paiement par carte est momentanément indisponible.', true);
      }
      return;
    }

    var submitBtn = modal.querySelector('[data-reserve-modal-submit]');
    if (submitBtn) submitBtn.disabled = true;
    setStatus(modal, 'Vérification de la carte…');

    var backend = modal.dataset.reserveBackend;

    mollieInstance
      .createToken()
      .then(function (result) {
        if (result.error) {
          setStatus(modal, result.error.message || 'Carte refusée. Vérifiez vos informations.', true);
          if (submitBtn) submitBtn.disabled = false;
          return;
        }
        if (!backend) {
          console.warn('[reserve-modal] settings.reserve_backend_url vide — paiement non branché.');
          setStatus(modal, 'Le paiement est momentanément indisponible.', true);
          if (submitBtn) submitBtn.disabled = false;
          return;
        }

        setStatus(modal, 'Réservation en cours…');
        var payload = collectFormData(modal, form);
        payload.cardToken = result.token;
        payload.stage = 'payment';

        // Attend un panier abandonné en vol avant de lire son identifiant : le
        // clic sur « Payer » a pu déclencher la synchro du dernier champ quitté.
        // Sans ça, deux brouillons pour un client — il en paie un, l'autre reste
        // en « panier abandonné » et un vendeur le relance pour rien.
        return whenLeadSettled()
          .then(function () {
            // Réutilise le brouillon créé à la saisie de l'e-mail : sans ça, chaque
            // réservation laisserait un panier abandonné fantôme à côté de la commande.
            var known = currentDraftId(modal);
            if (known) payload.draftOrderId = known;

            // Le backend RECALCULE le prix depuis Shopify : aucun montant d'ici.
            return fetch(backend, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
          })
          .then(readBackendJson)
          .then(function (data) {
            handlePaymentResponse(modal, payload.variantId, data, 'card', function () {
              if (submitBtn) submitBtn.disabled = false;
            });
          });
      })
      .catch(function (err) {
        console.error('[reserve-modal] paiement échoué:', err);
        setStatus(modal, 'Une erreur est survenue. Merci de réessayer.', true);
        if (submitBtn) submitBtn.disabled = false;
      });
  }

  // Tracking : la réservation part au paiement (3DS)
  function fireReservePaymentStarted(modal, data) {
    if (!window.dataLayer) return;
    pushEvent('reserve_payment_started', {
      reserve_modal: true,
      variant_id: modal.dataset.reserveVariantId || '',
      payment_id: data.paymentId || '',
      deposit_amount: data.deposit || '',
    });
  }

  // ---------------------------------------------------------------------------
  // goToCheckout — départ vers la page de paiement Mollie (3DS / wallet)
  //
  // La navigation reste DANS l'onglet (location.assign) : un anti-popup ne peut
  // pas la bloquer, contrairement à window.open après un fetch. Mais une webview
  // (Instagram, Facebook…) ou une extension peut l'étouffer sans lever d'erreur,
  // et le client resterait devant une modale figée alors que son paiement Mollie
  // existe déjà. Si la page est toujours là après le délai, on affiche un lien de
  // secours vers la MÊME page de paiement, on ré-adopte le brouillon que
  // beginCheckout venait d'oublier (sinon un réessai créerait un doublon) et on
  // réactive le bouton. Fausse alerte possible sur réseau très lent : le lien
  // s'affiche puis la navigation aboutit — sans effet de bord.
  // ---------------------------------------------------------------------------
  var CHECKOUT_RESCUE_DELAY_MS = 3000;

  function goToCheckout(modal, variantId, data, reenable) {
    fireReservePaymentStarted(modal, data);
    beginCheckout(modal);

    // Contexte pour l'écran de retour (reserve-confirmation) : Mollie renvoie
    // sur ?reserve=merci sans dire l'issue. Avec le paymentId stocké ici,
    // l'écran interroge le statut réel et peut proposer de réessayer — l'URL
    // produit (variant compris) est le lien de retry.
    try {
      window.sessionStorage.setItem(
        'reserveReturnContext',
        JSON.stringify({
          paymentId: data.paymentId || '',
          statusUrl: modal.dataset.reserveStatus || '',
          productUrl: window.location.href,
        }),
      );
    } catch (e) {
      /* sessionStorage indisponible — l'écran de retour restera neutre */
    }

    var gone = false;
    var onHide = function () {
      gone = true;
    };
    window.addEventListener('pagehide', onHide, { once: true });

    window.setTimeout(function () {
      window.removeEventListener('pagehide', onHide);
      if (gone || document.visibilityState === 'hidden') return;
      delete modal.dataset.reserveLeaving;
      adoptDraft(modal, variantId, data);
      showCheckoutRescue(modal, data.checkoutUrl);
      if (typeof reenable === 'function') reenable();
      window.dataLayer = window.dataLayer || [];
      pushEvent('reserve_redirect_blocked', {
        reserve_modal: true,
        variant_id: variantId || '',
        payment_id: data.paymentId || '',
      });
    }, CHECKOUT_RESCUE_DELAY_MS);

    // Redirection vers le paiement Mollie ; retour via redirectUrl.
    window.location.assign(data.checkoutUrl);
  }

  function showCheckoutRescue(modal, url) {
    var el = modal.querySelector('[data-reserve-modal-status]');
    if (!el) return;
    el.textContent = 'La page de paiement ne s’est pas ouverte ? ';
    var link = document.createElement('a');
    link.href = url;
    link.textContent = 'Ouvrir la page de paiement sécurisée';
    el.appendChild(link);
    el.classList.remove('reserve-modal__status--error');
    el.hidden = false;
  }

  // ---------------------------------------------------------------------------
  // click_cta — listener dédié des CTA de la modale
  //
  // OBLIGATOIRE : tracking-ui-forms.liquid:198 fait `return` sur tout élément
  // portant data-cta-name, en supposant qu'un listener dédié prend le relais.
  // Sans ce bloc, les boutons de la modale ne seraient plus trackés du tout.
  // ---------------------------------------------------------------------------
  function pushModalCta(modal, name, location, placement) {
    if (!name || !window.dataLayer) return;
    var payload = {
      event: 'click_cta',
      cta_name: name,
      cta_location: location || 'reserve_modal',
      variant_id: modal.dataset.reserveVariantId || '',
    };
    if (placement) payload.cta_placement = placement;
    window.dataLayer.push(payload);
  }

  function trackModalCta(modal, el) {
    pushModalCta(modal, el.getAttribute('data-cta-name'), el.getAttribute('data-cta-location'));
  }

  // ---------------------------------------------------------------------------
  // Wallets — disponibilité et paiement
  //
  // On FORCE la méthode côté Mollie (method: "applepay"…). Conséquence directe :
  // un bouton proposé sur un appareil incompatible ferait échouer la création du
  // paiement — d'où la détection par CAPACITÉ, jamais par taille d'écran.
  // Apple Pay marche aussi sur Safari macOS, Google Pay sur Chrome desktop :
  // filtrer sur « mobile » priverait ces utilisateurs du paiement en un clic.
  // ---------------------------------------------------------------------------
  // Valide les seules coordonnées : par wallet, les champs carte de Mollie sont
  // vides et form.checkValidity() les ignore de toute façon (ce sont des iframes).
  function validateContact(modal, form) {
    var champs = ['prenom', 'nom', 'email'];
    for (var i = 0; i < champs.length; i++) {
      var input = form[champs[i]];
      if (input && !input.checkValidity()) {
        input.reportValidity();
        return false;
      }
    }
    return true;
  }

  // Apple Pay : test recommandé par Mollie (docs.mollie.com/docs/apple-pay), qui
  // reprend celui d'Apple. Vrai uniquement sur matériel Apple, moteur WebKit et
  // Apple Pay configuré — donc iPhone/iPad et Safari macOS, jamais ailleurs.
  // PayPal ne dépend d'aucune capacité navigateur.
  function isWalletAvailable(method) {
    if (method === 'apple') {
      return Boolean(window.ApplePaySession && window.ApplePaySession.canMakePayments());
    }
    return true;
  }

  // Appareil Apple mobile ? iPadOS 13+ se déclare « MacIntel » : seul le nombre de
  // points de contact le distingue d'un vrai Mac.
  function isAppleMobile() {
    if (/iPhone|iPad|iPod/.test(navigator.userAgent)) return true;
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  }

  // ---------------------------------------------------------------------------
  // Google Pay — isReadyToPay(), seule détection que Google reconnaisse
  //
  // Sa doc écarte explicitement la détection par window.PaymentRequest, qui donne
  // des faux positifs : Safari l'implémente pour servir Apple Pay. Sniffer le
  // moteur Chromium ne vaut pas mieux — ça ne dit rien du portefeuille réel du
  // visiteur.
  //
  // pay.js n'est chargé qu'à l'ouverture de la modale, comme celui de Mollie :
  // aucun coût réseau sur le rendu de la fiche produit.
  // ---------------------------------------------------------------------------
  var GOOGLE_PAY_JS = 'https://pay.google.com/gp/p/js/pay.js';
  var googlePayLoading = null;
  var googlePayReady = null;

  function loadGooglePayScript() {
    if (window.google && google.payments && google.payments.api) return Promise.resolve();
    if (googlePayLoading) return googlePayLoading;

    googlePayLoading = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = GOOGLE_PAY_JS;
      script.async = true;
      script.onload = resolve;
      script.onerror = function () {
        reject(new Error('Google Pay script failed to load'));
      };
      document.head.appendChild(script);
    });
    return googlePayLoading;
  }

  function isGooglePayAvailable(testmode) {
    if (googlePayReady) return googlePayReady;

    googlePayReady = loadGooglePayScript()
      .then(function () {
        var client = new google.payments.api.PaymentsClient({
          environment: testmode ? 'TEST' : 'PRODUCTION',
        });
        // Requête minimale de disponibilité : pas de tokenizationSpecification,
        // on ne demande pas encore à payer. Renvoie vrai si le visiteur a une
        // carte enregistrée OU peut en ajouter une.
        return client.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [
            {
              type: 'CARD',
              parameters: {
                allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                allowedCardNetworks: ['VISA', 'MASTERCARD'],
              },
            },
          ],
        });
      })
      .then(function (res) {
        return Boolean(res && res.result);
      })
      .catch(function () {
        // Script bloqué, réseau coupé, API en erreur : on n'affiche pas un bouton
        // dont on ne peut pas garantir qu'il aboutira.
        return false;
      });

    return googlePayReady;
  }

  var MOLLIE_METHODS = { apple: 'applepay', google: 'googlepay', paypal: 'paypal' };

  // Le client part vers une autre surface (feuille wallet, page Mollie) : le
  // statut annonce OÙ il va plutôt qu'un « en cours » générique.
  var WALLET_STATUS = {
    apple: 'Ouverture d’Apple Pay sur la page sécurisée…',
    google: 'Ouverture de Google Pay sur la page sécurisée…',
    paypal: 'Ouverture de PayPal sur la page sécurisée…',
  };

  // Paiement par wallet : ni champs carte ni cardToken. Le backend crée le
  // brouillon puis le paiement Mollie, et on part sur le checkout hébergé où
  // le client valide dans son portefeuille.
  function payWithWallet(modal, form, btn, method) {
    var backend = modal.dataset.reserveBackend;
    if (!backend) {
      setStatus(modal, 'Le paiement est momentanément indisponible.', true);
      return;
    }
    if (!validateContact(modal, form)) return;

    btn.disabled = true;
    setStatus(modal, WALLET_STATUS[method] || 'Ouverture de la page de paiement sécurisée…');

    var payload = collectFormData(modal, form);
    payload.stage = 'payment';
    payload.method = MOLLIE_METHODS[method] || method;

    // Un panier abandonné peut être en vol : cliquer « Payer » fait perdre le
    // focus au dernier champ, ce qui déclenche la synchro juste avant. Sans cette
    // attente, le paiement partirait sans son draftOrderId et ouvrirait un second
    // brouillon — le client paierait l'un, l'autre resterait « panier abandonné ».
    whenLeadSettled()
      .then(function () {
        var known = currentDraftId(modal);
        if (known) payload.draftOrderId = known;

        return fetch(backend, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      })
      .then(readBackendJson)
      .then(function (data) {
        handlePaymentResponse(modal, payload.variantId, data, method, function () {
          btn.disabled = false;
        });
      })
      .catch(function (err) {
        console.error('[reserve-modal] wallet échoué:', err);
        setStatus(modal, 'Une erreur est survenue. Merci de réessayer.', true);
        btn.disabled = false;
      });
  }

  function collectFormData(modal, form) {
    return {
      prenom: form.prenom.value.trim(),
      nom: form.nom.value.trim(),
      email: form.email.value.trim(),
      tel: form.tel.value.trim(),
      countryCode: modal.dataset.reserveCountry || 'FR',
      variantId: modal.dataset.reserveVariantId || null,
      // Contexte du vélo, destiné à la NOTE HubSpot lue par le vendeur. Purement
      // informatif : le backend recalcule tous les montants depuis Shopify avant
      // d'encaisser quoi que ce soit, rien de financier ne dépend de ces valeurs.
      produit: modal.dataset.reserveProduitTitre || '',
      produitUrl: modal.dataset.reserveProduitUrl || '',
      acompte: textOf(modal, '[data-reserve-modal-amount]'),
      solde: textOf(modal, '[data-reserve-modal-balance]'),
      commandeTotal: modal.dataset.reserveCommandeTotal || '',
    };
  }

  // Lit un montant tel qu'il est AFFICHÉ au client, espaces insécables compris :
  // la note du vendeur montre ainsi exactement ce que le client avait sous les yeux.
  function textOf(modal, selector) {
    var el = modal.querySelector(selector);
    return el ? el.textContent.trim() : '';
  }

  // ---------------------------------------------------------------------------
  // Panier abandonné — le brouillon Shopify EST le panier abandonné
  //
  // L'API `abandonedCheckouts` est en lecture seule : impossible d'en créer un.
  // On crée donc un brouillon dès que l'e-mail est valide. Il est silencieux
  // (aucun e-mail tant qu'on n'appelle pas draftOrderInvoiceSend), invisible dans
  // les rapports de vente, et relançable. Au paiement, le même brouillon est
  // réutilisé — d'où le draftOrderId qu'on conserve ici.
  // ---------------------------------------------------------------------------
  var LEAD_STORAGE_KEY = 'mb-reserve-draft';

  function readStoredDraft(variantId) {
    try {
      var raw = window.sessionStorage.getItem(LEAD_STORAGE_KEY);
      if (!raw) return null;
      var saved = JSON.parse(raw);
      // Changement de taille = autre vélo : on repart sur un brouillon neuf.
      return saved.variantId === String(variantId) ? saved.draftOrderId : null;
    } catch (e) {
      return null;
    }
  }

  function storeDraft(variantId, draftOrderId) {
    try {
      window.sessionStorage.setItem(
        LEAD_STORAGE_KEY,
        JSON.stringify({ variantId: String(variantId), draftOrderId: draftOrderId }),
      );
    } catch (e) {
      /* sessionStorage indisponible : on garde seulement le dataset */
    }
  }

  function currentDraftId(modal) {
    return modal.dataset.reserveDraftId || readStoredDraft(modal.dataset.reserveVariantId) || '';
  }

  // Coordonnées saisies — stockées à part du brouillon : elles restent valables
  // même si le client change de taille ou de vélo, alors que le brouillon, lui,
  // est lié à un variant. Le client qui rouvre la modale ne retape rien.
  var CONTACT_STORAGE_KEY = 'mb-reserve-contact';
  var CONTACT_FIELDS = ['prenom', 'nom', 'email', 'tel'];

  function persistContact(form) {
    if (!form) return;
    var contact = {};
    var filled = false;
    CONTACT_FIELDS.forEach(function (name) {
      var field = form[name];
      var value = field && field.value ? field.value.trim() : '';
      contact[name] = value;
      if (value) filled = true;
    });
    if (!filled) return;
    try {
      window.sessionStorage.setItem(CONTACT_STORAGE_KEY, JSON.stringify(contact));
    } catch (e) {
      /* sessionStorage indisponible : simple confort, on abandonne en silence */
    }
  }

  function restoreContact(form) {
    if (!form) return;
    var contact = null;
    try {
      contact = JSON.parse(window.sessionStorage.getItem(CONTACT_STORAGE_KEY) || 'null');
    } catch (e) {
      return;
    }
    if (!contact) return;
    CONTACT_FIELDS.forEach(function (name) {
      var field = form[name];
      if (field && contact[name]) field.value = contact[name];
    });
  }

  // ---------------------------------------------------------------------------
  // beginCheckout(modal) — on part chez Mollie, plus rien ne doit être synchronisé
  //
  // La redirection déclenche `pagehide`, donc la synchro de sortie. Et comme le
  // brouillon vient d'être oublié (il devient une commande, un draftOrderUpdate y
  // échouerait), cette dernière synchro repartait à froid : le backend créait un
  // SECOND brouillon, fantôme, deux secondes après celui qu'on paie.
  // Constaté en réel le 2026-07-21 — paiement sur #D187, #D188 créé dans la foulée.
  // Le drapeau ferme les deux sources : la sortie de modale et le départ de champ.
  // ---------------------------------------------------------------------------
  function beginCheckout(modal) {
    modal.dataset.reserveLeaving = '1';
    clearStoredDraft(modal);
  }

  function isLeavingForCheckout(modal) {
    return Boolean(modal.dataset.reserveLeaving);
  }

  function clearStoredDraft(modal) {
    delete modal.dataset.reserveDraftId;
    delete modal.dataset.reserveLeadSignature;
    try {
      window.sessionStorage.removeItem(LEAD_STORAGE_KEY);
    } catch (e) {
      /* sessionStorage indisponible */
    }
  }

  // Synchro de lead en vol. Le paiement l'attend quand même : les deux touchent
  // le même contact HubSpot, et les enchaîner évite qu'une mise à jour de champ
  // parte après le paiement et réécrive des propriétés périmées.
  var pendingLead = null;

  function whenLeadSettled() {
    // syncLead avale ses propres erreurs : cette promesse ne rejette jamais.
    return pendingLead || Promise.resolve();
  }

  // Envoi silencieux : aucun retour visuel, l'échec est sans conséquence pour le client.
  function syncLead(modal, form) {
    var backend = modal.dataset.reserveBackend;
    if (!backend) return;

    var payload = collectFormData(modal, form);
    if (!payload.email || !payload.variantId) return;

    // Ne re-synchronise que si quelque chose a changé depuis le dernier envoi.
    var signature = JSON.stringify(payload);
    if (modal.dataset.reserveLeadSignature === signature) return;
    modal.dataset.reserveLeadSignature = signature;

    payload.stage = 'lead';
    // Première saisie → le backend crée le contact HubSpot ET une note. Champs
    // complétés ensuite → propriétés seules. Sans cette distinction, un client qui
    // renseigne nom puis téléphone se retrouverait avec trois notes identiques,
    // et le vendeur ne saurait plus laquelle lire.
    payload.evenement = modal.dataset.reserveLeadEnvoye ? 'mise_a_jour' : 'saisie';
    modal.dataset.reserveLeadEnvoye = '1';
    // Chaînage sur la synchro précédente : deux champs quittés coup sur coup
    // partiraient sinon en parallèle, tous deux sans draftOrderId, et créeraient
    // deux brouillons pour un seul client.
    pendingLead = whenLeadSettled()
      .then(function () {
        // ⚠️ Le drapeau se relit ICI, pas au déclenchement. Une synchro peut être
        // légitime quand l'événement part et ne plus l'être quand son tour arrive :
        // cliquer « Payer » fait perdre le focus au dernier champ, ce qui déclenche
        // une synchro qui patiente dans la file pendant que le paiement se monte —
        // et qui repartait à froid après beginCheckout, créant un brouillon fantôme.
        // Vécu deux fois le 2026-07-21 (#D188, puis #D190 malgré un premier
        // correctif posé, lui, au déclenchement). Ne pas remonter ce test.
        if (isLeavingForCheckout(modal)) return null;

        // L'identifiant se lit ICI et pas avant : le brouillon vient peut-être
        // d'être créé par la synchro qu'on attendait. Quand il existe, le backend
        // MET À JOUR au lieu d'ouvrir un second brouillon — c'est ce qui permet de
        // rejouer cette synchro à chaque champ complété, un téléphone ajouté après
        // coup enrichissant le panier existant.
        var known = currentDraftId(modal);
        if (known) payload.draftOrderId = known;

        return fetch(backend, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        });
      })
      .then(function (res) {
        // res vaut null quand la synchro a été abandonnée juste au-dessus.
        return res ? readBackendJson(res) : null;
      })
      .then(function (data) {
        if (!data || !data.draftOrderId) return;
        modal.dataset.reserveDraftId = data.draftOrderId;
        storeDraft(payload.variantId, data.draftOrderId);
      })
      .catch(function () {
        // Panier abandonné best-effort : ne jamais bloquer ni alerter le client.
        modal.dataset.reserveLeadSignature = '';
      });

    return pendingLead;
  }

  // ---------------------------------------------------------------------------
  // Listener principal — CAPTURE synchrone top-level
  // Intercepte AVANT les handlers bubble de product-selling-plan-form.liquid
  // et product-form-fmd.liquid. Sélecteur identique au tracker GTM.
  // ---------------------------------------------------------------------------
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest('button[name="reserve"], .reserve-btn');
      if (!btn) return;

      // KILL SWITCH — réglage « Reservation experience » du thème.
      // Si la modale est désactivée, on ne touche À RIEN : pas de
      // stopImmediatePropagation, donc les handlers de
      // product-selling-plan-form.liquid et product-form-fmd.liquid
      // s'exécutent normalement et rouvrent l'ancien drawer #ReserveDrawer.
      // Retour à l'état d'avant sans redéploiement.
      if (!isModalExperienceEnabled()) return;

      // Stopper tous les autres listeners (product-selling-plan-form + product-form-fmd)
      e.stopImmediatePropagation();
      e.preventDefault();

      var variantId = resolveVariantId(btn);
      fireReserveTracking(variantId);

      // Réactiver le bouton si un handler concurrent l'a passé disabled avant interception
      btn.disabled = false;

      openModal(btn, variantId);
    },
    true, // Phase CAPTURE
  );

  // ---------------------------------------------------------------------------
  // Listeners internes à la modale. Le listener capture ci-dessus est attaché
  // immédiatement ; ceux-ci au DOMContentLoaded — sûr, car un script defer
  // s'exécute AVANT cet événement et la modale est déjà dans le DOM.
  // ---------------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', function () {
    var modal = document.getElementById('ReserveModalOverlay');
    if (!modal) return;

    var closeBtn = modal.querySelector('[data-reserve-modal-close]');
    if (closeBtn) closeBtn.addEventListener('click', closeModal);

    var backdrop = modal.querySelector('[data-reserve-modal-backdrop]');
    if (backdrop) backdrop.addEventListener('click', closeModal);

    var form = modal.querySelector('[data-reserve-modal-form]');
    if (form) {
      form.addEventListener('submit', handleSubmit);
      form.addEventListener('input', function () {
        modal.dataset.reserveTouched = '1';
      });
    }

    var pickup = pickupInput(modal);
    if (pickup) {
      pickup.addEventListener('change', function () {
        syncPickupMode(modal);
        // Un message d'erreur de carte, ou l'avis de repli hébergé, n'a plus
        // d'objet dès qu'on quitte l'acompte.
        setStatus(modal, '');
        firePickupToggle(modal, pickup.checked);
      });
    }

    var emailInput = modal.querySelector('#reserve-modal-email');

    // ---------------------------------------------------------------------
    // Panier abandonné : créé dès que l'e-mail est VALIDE, sans attendre que
    // le client ferme la modale. Attendre la fermeture perdait tous ceux qui
    // abandonnent l'onglet, le referment d'un balayage ou voient leur mobile
    // s'endormir — c'est-à-dire la majorité des abandons.
    //
    // Rejoué au départ de CHAQUE champ : le brouillon existant est alors mis à
    // jour (le backend bascule sur draftOrderUpdate), donc un téléphone ajouté
    // après coup enrichit le panier au lieu d'en ouvrir un second.
    //
    // `change` et non `input` : il ne se déclenche qu'à la perte de focus, et
    // seulement si la valeur a bougé. Une frappe ne part jamais au réseau.
    // ---------------------------------------------------------------------
    function syncLeadFromField() {
      if (!form || !emailInput) return;
      if (isLeavingForCheckout(modal)) return;
      if (!emailInput.validity.valid || !emailInput.value) return;
      persistContact(form);
      syncLead(modal, form);
    }

    if (emailInput) {
      // Message explicite sur domaine sans extension (« cfmosa@gmail ») : le natif
      // dirait « Veuillez respecter le format demandé », ce qui n'aide personne à
      // comprendre qu'il manque « .com ». Reposé à chaque frappe, sinon un message
      // personnalisé rend le champ invalide À VIE (setCustomValidity non vidé).
      emailInput.addEventListener('input', function () {
        emailInput.setCustomValidity('');
        if (emailInput.validity.patternMismatch) {
          emailInput.setCustomValidity("Il manque l'extension du domaine — par exemple @gmail.com et non @gmail.");
        }
      });

      emailInput.addEventListener('change', function () {
        if (!emailInput.validity.valid || !emailInput.value) return;
        fireEmailCaptured(modal, emailInput.value.trim());
      });
    }

    CONTACT_FIELDS.forEach(function (name) {
      var field = form && form[name];
      if (field) field.addEventListener('change', syncLeadFromField);
    });

    // Filet de sécurité : le client peut partir sans jamais quitter un champ
    // (clic direct sur la croix depuis le dernier champ saisi). On capte les
    // trois sorties — croix/backdrop/Échap, onglet masqué, fermeture de page.
    // `keepalive` sur le fetch laisse la requête aboutir même si le document
    // est en train de disparaître.
    function syncLeadOnExit() {
      if (isLeavingForCheckout(modal)) return;
      if (!modal.dataset.reserveTouched) return;
      persistContact(form);
      if (!form || !emailInput || !emailInput.validity.valid || !emailInput.value) return;
      syncLead(modal, form);
    }

    modalExitHandlers.push(syncLeadOnExit);

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden' && isModalOpen(modal)) syncLeadOnExit();
    });
    window.addEventListener('pagehide', function () {
      if (isModalOpen(modal)) syncLeadOnExit();
    });

    // Tracking des CTA de la modale (cf. trackModalCta : le handler générique
    // du thème les ignore dès qu'ils portent data-cta-name).
    modal.addEventListener('click', function (e) {
      var cta = e.target.closest('[data-cta-name]');
      if (cta && modal.contains(cta)) trackModalCta(modal, cta);
    });

    // Plus aucun wallet disponible : la rangée n'a plus lieu d'être.
    function syncWalletsZone() {
      var zone = modal.querySelector('.reserve-modal__wallets');
      if (!zone) return;
      zone.hidden = !modal.querySelectorAll('[data-reserve-modal-wallet]:not([hidden])').length;
    }

    // Wallets — la méthode est FORCÉE côté Mollie (method: "applepay"…), donc un
    // bouton affiché à tort mène le client sur un checkout hébergé qui ne peut pas
    // aboutir. On masque ceux que l'appareil ne sait pas honorer, puis on les branche.
    var wallets = modal.querySelectorAll('[data-reserve-modal-wallet]');
    Array.prototype.forEach.call(wallets, function (wallet) {
      var method = wallet.dataset.reserveModalWallet;
      wallet.addEventListener('click', function () {
        payWithWallet(modal, form, wallet, method);
      });

      if (method !== 'google') {
        wallet.hidden = !isWalletAvailable(method);
        return;
      }

      // ⚠️ Sur iPhone/iPad, Google Pay est écarté alors qu'il FONCTIONNE :
      // l'API web de Google est bel et bien supportée par Safari (flux en popup,
      // connexion au compte Google), et isReadyToPay() y répond `true` à juste
      // titre. C'est donc un choix produit et non une contrainte technique — sur
      // un appareil Apple le client attend Apple Pay, lui proposer les deux ne
      // fait qu'obscurcir la décision. Ne pas « corriger » ce test en croyant à
      // une incompatibilité : il n'y en a pas.
      // Court-circuit avant le chargement de pay.js, autant ne pas le télécharger.
      if (isAppleMobile()) {
        wallet.hidden = true;
        return;
      }

      // isReadyToPay() est asynchrone : le bouton part MASQUÉ et n'apparaît que
      // s'il est utilisable. Jamais l'inverse — mieux vaut un bouton qui surgit
      // qu'un bouton qui disparaît sous le doigt du client.
      wallet.hidden = true;
      isGooglePayAvailable(modal.dataset.mollieTestmode === 'true').then(function (ready) {
        if (!ready) return;
        wallet.hidden = false;
        syncWalletsZone();
      });
    });

    syncWalletsZone();
  });
})();
